<template>
  <form
    :action="formAction"
    :method="effectiveMethod === 'get' ? 'get' : 'post'"
    class="form-horizontal vxlan-form-layout"
  >
    <input v-if="csrfToken && effectiveMethod !== 'get'" type="hidden" name="authenticity_token" :value="csrfToken">
    <input v-if="effectiveMethod !== 'post' && effectiveMethod !== 'get'" type="hidden" name="_method" :value="effectiveMethod">

    <div class="form-group row" :class="{ 'vxlan-field-invalid': !!nameError }">
      <label class="col-sm-3 control-label" for="vxlan_name">Name</label>
      <div class="col-sm-9">
        <input id="vxlan_name" v-model="name" name="vxlan[name]" type="text" autocomplete="off" class="form-control">
        <p v-if="nameError" class="vxlan-inline-error">{{ nameError }}</p>
      </div>
    </div>

    <div class="form-group row" :class="{ 'vxlan-field-invalid': !!vniError }">
      <label class="col-sm-3 control-label" for="vxlan_vni">VNI</label>
      <div class="col-sm-9">
        <input
          id="vxlan_vni"
          v-model="vni"
          name="vxlan[vni]"
          type="text"
          autocomplete="off"
          inputmode="numeric"
          :placeholder="autoVni ? 'Optional. Auto assign from 5001 - 65535' : '5001 - 16777214'"
          class="form-control"
        >
        <p v-if="vniError" class="vxlan-inline-error">{{ vniError }}</p>
      </div>
    </div>

    <div class="form-group row">
      <label class="col-sm-3 control-label" for="vxlan_description">Description</label>
      <div class="col-sm-9">
        <input id="vxlan_description" v-model="description" name="vxlan[description]" type="text" class="form-control">
      </div>
    </div>

    <div class="form-group boolean optional vxlan-visibility-group">
      <label class="col-sm-3 control-label">Visibility</label>
      <div class="col-sm-9">
        <input type="hidden" name="vxlan[public]" value="false">
        <label class="vxlan-visibility-toggle">
          <input v-model="publicChecked" name="vxlan[public]" type="checkbox" value="true">
          <span class="vxlan-visibility-slider" aria-hidden="true"></span>
          <span class="vxlan-visibility-copy">
            <strong>Public VXLAN</strong>
            <small>Enable when this VXLAN should be available to all users.</small>
          </span>
        </label>
      </div>
    </div>

    <div class="form-group boolean optional vxlan-visibility-group">
      <label class="col-sm-3 control-label">EVPN Type-5</label>
      <div class="col-sm-9">
        <input type="hidden" name="vxlan[type5_enabled]" value="false">
        <label class="vxlan-visibility-toggle">
          <input v-model="type5Enabled" name="vxlan[type5_enabled]" type="checkbox" value="true">
          <span class="vxlan-visibility-slider" aria-hidden="true"></span>
          <span class="vxlan-visibility-copy">
            <strong>Advertise Type-5 prefixes</strong>
            <small>Private VXLANs use a VRF managed on the VRF page.</small>
          </span>
        </label>
      </div>
    </div>
    <template v-if="type5Enabled">
<div v-if="!publicChecked" class="vxlan-vrf-selection">
        <div class="form-group row" :class="{ 'vxlan-field-invalid': !!evpnVrfSelectionError }">
          <label class="col-sm-3 control-label" for="vxlan_evpn_vrf_id">L3VNI / VRF</label>
          <div class="col-sm-9">
            <VmNicSelect
              :options="evpnVrfOptions"
              :initial-value="evpnVrfId"
              input-name="vxlan[evpn_vrf_id]"
              :empty-label="evpnVrfs.length ? 'Select an L3VNI / VRF' : 'No VRFs available'"
              filter-placeholder="Filter L3VNIs / VRFs"
              :readonly="!evpnVrfs.length"
              @update:value="evpnVrfId = $event"
            />
            <p v-if="!evpnVrfs.length" class="help-block">Create a VRF before enabling Type-5 for a private VXLAN. <a :href="evpnVrfsUrl">Manage VRFs</a></p>
            <p v-else class="help-block"><a :href="evpnVrfsUrl">Manage VRFs</a></p>
            <p v-if="evpnVrfSelectionError" class="vxlan-inline-error">{{ evpnVrfSelectionError }}</p>
          </div>
        </div>
      </div>
      <div class="form-group row" :class="{ 'vxlan-field-invalid': !!ipv4PrefixError }">
        <label class="col-sm-3 control-label" for="vxlan_ipv4">IPv4 Prefix</label>
        <div class="col-sm-9">
          <input id="vxlan_ipv4" v-model="ipv4" name="vxlan[ipv4]" type="text" class="form-control" placeholder="192.0.2.0/24">
          <p v-if="ipv4PrefixError" class="vxlan-inline-error">{{ ipv4PrefixError }}</p>
        </div>
      </div>
      <div class="form-group row" :class="{ 'vxlan-field-invalid': !!gatewayIpv4Error }">
        <label class="col-sm-3 control-label" for="vxlan_gateway_ipv4">IPv4 Gateway</label>
        <div class="col-sm-9">
          <input id="vxlan_gateway_ipv4" v-model="gatewayIpv4" name="vxlan[gateway_ipv4]" type="text" class="form-control" placeholder="192.0.2.1">
          <p v-if="gatewayIpv4Error" class="vxlan-inline-error">{{ gatewayIpv4Error }}</p>
        </div>
      </div>
      <div class="form-group row" :class="{ 'vxlan-field-invalid': !!ipv6PrefixError }">
        <label class="col-sm-3 control-label" for="vxlan_ipv6">IPv6 Prefix</label>
        <div class="col-sm-9">
          <input id="vxlan_ipv6" v-model="ipv6" name="vxlan[ipv6]" type="text" class="form-control" placeholder="2001:db8:1::/64">
          <p v-if="ipv6PrefixError" class="vxlan-inline-error">{{ ipv6PrefixError }}</p>
        </div>
      </div>
      <div class="form-group row" :class="{ 'vxlan-field-invalid': !!gatewayIpv6Error }">
        <label class="col-sm-3 control-label" for="vxlan_gateway_ipv6">IPv6 Gateway</label>
        <div class="col-sm-9">
          <input id="vxlan_gateway_ipv6" v-model="gatewayIpv6" name="vxlan[gateway_ipv6]" type="text" class="form-control" placeholder="2001:db8:1::1">
          <p v-if="gatewayIpv6Error" class="vxlan-inline-error">{{ gatewayIpv6Error }}</p>
        </div>
      </div>
    </template>

    <div class="form-actions">
      <button type="submit" class="btn btn-primary" :disabled="submitDisabled">{{ submitLabel }}</button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref } from "vue"
import VmNicSelect from "./VmNicSelect.vue"

const props = defineProps({
  formAction: {
    type: String,
    required: true
  },
  formMethod: {
    type: String,
    default: "post"
  },
  submitLabel: {
    type: String,
    default: "Save"
  },
  initialName: {
    type: String,
    default: ""
  },
  initialVni: {
    type: [String, Number],
    default: ""
  },
  initialDescription: {
    type: String,
    default: ""
  },
  initialPublic: {
    type: Boolean,
    default: false
  },
  initialType5Enabled: {
    type: Boolean,
    default: false
  },
  initialIpv4: {
    type: String,
    default: ""
  },
  initialIpv6: {
    type: String,
    default: ""
  },
  initialGatewayIpv4: {
    type: String,
    default: ""
  },
  initialGatewayIpv6: {
    type: String,
    default: ""
  },
  existingVnis: {
    type: Array,
    default: () => []
  },
  existingL3Vnis: {
    type: Array,
    default: () => []
  },
  initialEvpnVrfId: { type: [String, Number], default: "" },
  evpnVrfs: { type: Array, default: () => [] },
  evpnVrfsUrl: { type: String, default: "/evpn_vrfs" },
  prefixAllocations: { type: Object, default: () => ({}) },
  autoVni: {
    type: Boolean,
    default: false
  }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const effectiveMethod = props.formMethod.toLowerCase()
const name = ref(props.initialName)
const vni = ref(String(props.initialVni || ""))
const description = ref(props.initialDescription)
const publicChecked = ref(!!props.initialPublic)
const type5Enabled = ref(!!props.initialType5Enabled)
const ipv4 = ref(props.initialIpv4)
const ipv6 = ref(props.initialIpv6)
const gatewayIpv4 = ref(props.initialGatewayIpv4)
const gatewayIpv6 = ref(props.initialGatewayIpv6)
const evpnVrfId = ref(String(props.initialEvpnVrfId || ""))
const evpnVrfOptions = computed(() =>
  (props.evpnVrfs || []).map((vrf) => ({
    label: `${vrf.vni} · ${vrf.name}`,
    value: String(vrf.id)
  }))
)
const initialVniValue = String(props.initialVni || "").trim()
const normalizedExistingVnis = computed(() =>
  (props.existingVnis || []).map((value) => String(value || "").trim()).filter(Boolean)
)
const normalizedExistingL3Vnis = computed(() =>
  (props.existingL3Vnis || []).map((value) => String(value || "").trim()).filter(Boolean)
)
const normalizedName = computed(() => String(name.value || "").trim())
const normalizedVni = computed(() => String(vni.value || "").trim())

function parseIpv4(value) {
  const parts = String(value || "").split(".")
  if (parts.length !== 4 || parts.some((part) => !/^\d+$/.test(part))) return null
  const numbers = parts.map(Number)
  if (numbers.some((part) => part < 0 || part > 255)) return null
  return numbers.reduce((result, part) => (result << 8n) | BigInt(part), 0n)
}

function parseIpv6(value) {
  const text = String(value || "").toLowerCase()
  if (!text || text.includes("%") || text.includes(".")) return null
  if ((text.match(/::/g) || []).length > 1) return null

  const sides = text.split("::")
  const left = sides[0] ? sides[0].split(":") : []
  const right = sides.length === 2 && sides[1] ? sides[1].split(":") : []
  if (sides.length === 1 && left.length !== 8) return null
  if (sides.length === 2 && left.length + right.length >= 8) return null

  const groups = sides.length === 2
    ? [...left, ...Array(8 - left.length - right.length).fill("0"), ...right]
    : left
  if (groups.length !== 8 || groups.some((group) => !/^[0-9a-f]{1,4}$/.test(group))) return null

  return groups.reduce((result, group) => (result << 16n) | BigInt(parseInt(group, 16)), 0n)
}

function parseHost(value, family) {
  if (String(value || "").includes("/")) return null
  return family === 4 ? parseIpv4(value) : parseIpv6(value)
}

function parseCidr(value, family) {
  const parts = String(value || "").trim().split("/")
  if (parts.length !== 2 || !/^\d+$/.test(parts[1])) return null

  const bits = family === 4 ? 32 : 128
  const prefixLength = Number(parts[1])
  if (prefixLength < 0 || prefixLength > bits) return null

  const address = family === 4 ? parseIpv4(parts[0]) : parseIpv6(parts[0])
  if (address === null) return null

  const hostBits = BigInt(bits - prefixLength)
  const allBits = (1n << BigInt(bits)) - 1n
  const hostMask = hostBits === 0n ? 0n : (1n << hostBits) - 1n
  const network = address & (allBits ^ hostMask)
  return { address, network, last: network | hostMask, prefixLength }
}

function rangesOverlap(left, right) {
  return left.network <= right.last && right.network <= left.last
}

function validatePrefixField(value, family, allocations, attribute, requireAtLeastOne) {
  if (!type5Enabled.value) return ""
  const raw = String(value || "").trim()
  if (!raw) return requireAtLeastOne ? "Type-5 requires an IPv4 or IPv6 prefix." : ""

  const parsed = parseCidr(raw, family)
  if (!parsed) return `Enter a valid IPv${family} prefix with a prefix length.`
  if (parsed.address !== parsed.network) return `IPv${family} Prefix must be a network address.`

  for (const allocation of allocations || []) {
    const existingValue = String(allocation?.[attribute] || "").trim()
    if (!existingValue) continue
    const existing = parseCidr(existingValue, family)
    if (existing && rangesOverlap(parsed, existing)) {
      return `This prefix overlaps ${existingValue} used by ${allocation.name} in the same VRF.`
    }
  }
  return ""
}

function validateGatewayField(gatewayValue, prefixValue, family) {
  if (!type5Enabled.value) return ""
  const gatewayRaw = String(gatewayValue || "").trim()
  const prefixRaw = String(prefixValue || "").trim()

  if (!prefixRaw) return gatewayRaw ? `Configure an IPv${family} prefix before its gateway.` : ""
  if (!gatewayRaw) return `IPv${family} Gateway is required when the prefix is configured.`

  const prefix = parseCidr(prefixRaw, family)
  if (!prefix || prefix.address !== prefix.network) return ""

  const gateway = parseHost(gatewayRaw, family)
  if (gateway === null) return `Enter a valid IPv${family} host address without a prefix length.`
  if (gateway < prefix.network || gateway > prefix.last) return `Gateway must be inside the configured IPv${family} prefix.`

  const unusable = family === 4
    ? prefix.prefixLength < 31 && (gateway === prefix.network || gateway === prefix.last)
    : prefix.prefixLength < 128 && gateway === prefix.network
  return unusable ? "Gateway is not a usable host address in the configured prefix." : ""
}

const nameError = computed(() => {
  if (!normalizedName.value) {
    return "Name is required."
  }
  return ""
})

const vniError = computed(() => {
  const value = normalizedVni.value

  if (!value && !props.autoVni) {
    return "VNI is required."
  }
  if (!value) {
    return ""
  }
  if (!/^\d+$/.test(value)) {
    return "VNI must be a number."
  }
  const parsed = parseInt(value, 10)
  if (parsed < 5001 || parsed > 16777214) {
    return "VNI must be between 5001 and 16777214."
  }
  if (value !== initialVniValue && normalizedExistingVnis.value.includes(value)) {
    return "This VNI is already used as an L2VNI."
  }
  if (normalizedExistingL3Vnis.value.includes(value)) {
    return value === "5000" ? "VNI 5000 is reserved for public EVPN." : "This VNI is already used as an L3VNI."
  }
  return ""
})

const evpnVrfSelectionError = computed(() => {
  if (!type5Enabled.value || publicChecked.value) return ""
  return evpnVrfId.value ? "" : "Select an L3VNI / VRF."
})
const selectedVrfAllocations = computed(() => {
  const key = publicChecked.value ? "public" : `vrf:${evpnVrfId.value}`
  return props.prefixAllocations?.[key] || []
})

const ipv4PrefixError = computed(() => validatePrefixField(ipv4.value, 4, selectedVrfAllocations.value, "ipv4", !String(ipv6.value || "").trim()))
const ipv6PrefixError = computed(() => validatePrefixField(ipv6.value, 6, selectedVrfAllocations.value, "ipv6", false))
const gatewayIpv4Error = computed(() => validateGatewayField(gatewayIpv4.value, ipv4.value, 4))
const gatewayIpv6Error = computed(() => validateGatewayField(gatewayIpv6.value, ipv6.value, 6))
const submitDisabled = computed(() =>
  !!nameError.value ||
  !!vniError.value ||
  !!evpnVrfSelectionError.value ||
  !!ipv4PrefixError.value ||
  !!ipv6PrefixError.value ||
  !!gatewayIpv4Error.value ||
  !!gatewayIpv6Error.value
)
</script>
